import superagent from 'superagent';
import {v4 as uniqueId} from 'uuid';
import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {getFuzzyLocalTimeFromPoint} from '@mapbox/timespace';
import {zipSync, strToU8} from 'fflate';
import {dayOfYear} from '../util/dayOfYear';
import {getPosixStringForCurrentYear} from '../util/posixTime';

export const LocationState= {
	notSearched: 'notSearched',
	valid: 'valid',
	notFound: 'notFound',
	outOfRange: 'outOfRange'
};
export const fetchProfileResult = createAsyncThunk(
	'lightProfile/fetchProfileResult',
	(options, {getState}) =>
		superagent
			.post(`${getState().status.expressUrl}/lightprofile`)
			.send(options)
			.set('Accept', 'application/json')
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchLightProfileLocation = createAsyncThunk(
	'lightProfile/fetchLightProfileLocation',
	(options, {getState}) =>
		superagent
			.get('https://maps.googleapis.com/maps/api/geocode/json?address=' + options.address + '&key=AIzaSyAulSCwO2HXl5YuTFc_Qy43XMT-Yc2KRao')
			.set('Accept', 'application/json')
			.then((response) => response.body)
			.catch((error) => error)
);


const lightProfilesInitialState = {
	status: 'idle',
	data: {
		project: {
			location: {
				name: 'Berlin',
				latitude: 52.52,
				longitude: 13.40,
				timezone: 'CET-1CEST,M3.4.0,M10.5.0/3',
				timezoneName: 'Europe/Berlin',
				offset: 0,
			},
			base: null,
			zone: null,
			luminary: null,
			picoPassword: uniqueId().slice(-10),
			projectName: '',
			centralPowerControl: 'off'
		},
		channelId: 'channel0',
		profile: null,
		simulationResult: null,
		dailySegments: [],
		selectedDay: 0,
		selectedSecond: 0,
		simulationIndex: 0,
		locationState: LocationState.valid,
		draggedOver: false,
		fileDropped: false,
		fileName: 'Drag\'n\'Drop ZIP',
	},
	error: {},
};
export const lightProfileSlice = createSlice({
	name: 'lightProfile',
	initialState: lightProfilesInitialState,
	reducers: {
		setLocation: (state, action) => {
			state.data.project.location = action.payload.location;
		},
		setCentralPowerControlId: (state, action) => {
			state.data.project.centralPowerControl = action.payload.id;
		},
		setBaseProfileId: (state, action) => {
			state.data.project.base = action.payload.id;
		},
		setZoneId: (state, action) => {
			state.data.project.zone = action.payload.id;
		},
		setLuminaryId: (state, action) => {
			state.data.project.luminary = action.payload.id;
		},
		setProfileData: (state, action) => {
			state.data.project = action.payload.project;
			state.data.simulationIndex = state.data.simulationIndex + 1;
		},
		setLoadedData: (state, action) => {
			state.data.project.base = action.payload.base;
			state.data.project.zone = action.payload.zone;
			state.data.project.centralPowerControl = action.payload.centralPowerControl;
			state.data.project.location = action.payload.location;
		},

		setChannelId: (state, action) => {
			if (state.data.channelId !== action.payload.channelId) {
				state.data.channelId = action.payload.channelId;
				state.data.simulationIndex = state.data.simulationIndex + 1;
			}
		},
		setSelectedDay: (state, action) => {
			state.data.selectedDay = action.payload.selectedDay;
			state.data.selectedSecond = action.payload.selectedSecond;
		},
		setProfile: (state, action) => {
			state.data.profile = action.payload.profile;
		},
		setProjectName: (state, action) => {
			state.data.project.projectName = action.payload.text;
		},
		setPicoPassword: (state, action) => {
			state.data.project.picoPassword = action.payload.password ?? uniqueId().slice(-10);
		},
		saveSimJson: (state, action) => {
			state.simJson = action.payload;
		},
		customUpload: (state) => {
			state.draggedOver = true;
			state.fileDropped = true;
			state.fileName = '';
		},
		saveCustomUploadStatus: (state, action) => {
			state.customUploadStatus = {
			  status: action.payload.status,
			};
		},
		resetCustomUpload: (state) => {
			state.draggedOver = false;
			state.fileDropped = false;
			state.fileName = 'Drag\'n\'Drop ZIP';
			state.simJson = null;
			state.customUploadStatus = {
			  status: null,
			};
		},

		downloadProfile: (state, action) => {
			const exportObj = Object.assign({}, state.data.profile);
			exportObj.project = state.data.project;
			const exportName = state.data.project.base + '_' + state.data.project.zone + '_' +state.data.project.location.name;
			downloadAsJson(exportObj, exportName);
		},
		downloadSimulationResult: (state, action) => {
			const simulationResult = state.data.simulationResult;
			simulationResult.targets.forEach(target => {
				target.keyframes.forEach(keyframe => {
					keyframe[0] = new Date(keyframe[0] * 1000).toLocaleDateString('de', {
						year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
					});
				});
			});
			const keyframesName = state.data.project.base + '_' + state.data.project.zone + '_' +state.data.project.location.name + '_simulation';
			downloadAsJson(simulationResult, keyframesName);
		},
		downloadZip: (state, action) => {
			let setupName = state.data.project.base + '_' + state.data.project.zone + '_' +state.data.project.location.name;
			setupName = setupName.replace(/[/|\\:*?"<>]/g, ' ');
			const conf9 = {
				pico: {
					timezone: state.data.project.location.timezone,
					latitude: '' + state.data.project.location.latitude,
					longitude: '' + state.data.project.location.longitude,
					setupfilename: 'setup.json',
					enablewifiapatboot: true
				},
				wifi: {
					ap: {
						disable_after: 900,
						enable: true,
						gw: '192.168.4.1',
						ip: '192.168.4.1',
						pass: state.data.project.picoPassword
					},
					sta: {
						enable: true,
						pass: 'mu9srb2svmeur0i',
						ssid: 'selux_tc'
					},
				},
				comment: {
					projectname: action.payload.info.projectName,
					project: state.data.project,
				},
				mqtt: {
					enable: true,
					pass: 'cZdVHTj8Jw2Jcs',
					server: '192.168.1.1',
					user: 'picomqtt'
				}
			};
			// create zip with fflate
			const zip = zipSync(
				{
					'conf9.json': strToU8(JSON.stringify(conf9, null, 2)),
					'setup.json': strToU8(JSON.stringify(state.data.profile.setup, null, 2)),
				}
			);
			const blob = new Blob([zip], {type: 'application/zip'});
			downloadAsZip(blob, setupName);

		}
	},
	extraReducers: (builder) => {
		builder.addCase(fetchProfileResult.fulfilled, (state, action) => {
			state.data.simulationResult = action.payload;
			state.data.simulationIndex = state.data.simulationIndex + 1;
			if (action.payload.targets.length > 0) {
				state.data.channelId = 'channel0';
			}
			state.data.dailySegments = action.payload.targets.map(target => (
				{
					targetId: target.id,
					type: target.type,
					format: target.format,
					days: createDailySegments(target, state.data.project.location, state.data.project.location.timezoneName)
				}
			));
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchProfileResult.pending, (state, action) => {
			state.data.simulationResult = null;
			state.data.dailySegments = null;
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchProfileResult.rejected, (state, action) => {
			state.data.simulationResult = null;
			state.data.dailySegments = null;
			state.status = 'idle';
			state.error = action.payload;
		});
		builder.addCase(fetchLightProfileLocation.fulfilled, (state, action) => {
			if (action.payload.results.length > 0) {
				const lat = action.payload.results[0].geometry.location.lat;
				// TODO: display message if location is out of range
				if ((lat < 65 && lat > 25) || (lat > -65 && lat < -25)) {
					state.data.project.location  = {};
					const addressComponents = action.payload.results[0].address_components.filter(ac => ac.types.includes('sublocality') || ac.types.includes('locality') || ac.types.includes('country'));
					state.data.project.location.name = addressComponents[0].long_name;
					state.data.project.location.latitude = Math.round(action.payload.results[0].geometry.location.lat * 1000) / 1000;
					state.data.project.location.longitude = Math.round(action.payload.results[0].geometry.location.lng * 1000) / 1000;
					const timezone = getFuzzyLocalTimeFromPoint(Date.now(), [state.data.project.location.longitude, state.data.project.location.latitude]);
					const myOffset = new Date().getTimezoneOffset();
					state.data.project.location.timezone = getPosixStringForCurrentYear(timezone._z.name);
					state.data.project.location.timezoneName = timezone._z.name;
					state.data.project.location.offset = timezone._offset + myOffset;
					state.data.locationOutOfRange = false;
					state.data.locationState = LocationState.valid;

				} else {
					state.data.project.location = null;
					state.data.locationState = LocationState.outOfRange;
				}
			} else {
				state.data.locationState = LocationState.notFound;
				state.data.project.location = null;
			}
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchLightProfileLocation.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchLightProfileLocation.rejected, (state, action) => {
			state.data.project.location = null;
			state.status = 'idle';
			state.error = action.payload;
		});
	}
});

function createDailySegments(target, location, timezoneName) {
	const days = [];
	let prevBrightness = 0;
	let prevStart = 0;
	let prevDay = 0;
	target.keyframes.forEach(keyframe => {
		const ts = keyframe[0];
		const d = new Date(ts * 1000 - 12 * 3600 * 1000);
		const localdatestring = d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium', timeZone: timezoneName });
		const locald = new Date(Date.parse(localdatestring));
		const brightness = keyframe[2];
		const cct = keyframe[3];
		const day = dayOfYear(locald) - 1;
		const gap = day - prevDay;
		const start = (locald.getHours() * 3600 + d.getMinutes() * 60 + locald.getSeconds()) / (24 * 3600 + 60 * 60 + 60);
		let fade = Math.max(0, keyframe[1] / (24 * 3600 + 60 * 60 + 60)); // keyframe[1]
		fade = Math.min(fade, start - prevStart);
		if (days[day] === undefined) {
			days[day] = [];
			// add first element, if there is nothing at start
			if (start > 0) {
				days[day].push(
					{
						start: (23 / 24)*0.5,
						size: null,
						brightness: gap > 1?0:prevBrightness,
						prevBrightness: gap > 1?0:prevBrightness,
						cct: cct
					}
				);
			}
		}
		if (fade > 0) {
			days[day].push({
				start: start,
				size: null,
				brightness: brightness,
				prevBrightness: prevBrightness,
				cct: cct
			});
			days[day].push({
				start: start + fade,
				size: null,
				brightness: brightness,
				prevBrightness: brightness,
				cct: cct
			});
		} else {
			// add frame without fade
			days[day].push({
				start: start,
				size: null,
				brightness: brightness,
				prevBrightness: brightness,
				cct: cct
			});
		}
		prevBrightness = brightness;
		prevStart = start;
		prevDay = day;
	});

	// calculate sizes
	days.forEach(day => day.forEach((segment, index) => {
		if (index < day.length - 1) {
			// item has follower
			day[index].size = day[index + 1].start - day[index].start;
		} else {
			// last item
			if (day[index].start < 0.5) {
				day[index].size = (23 / 24)*0.5 - day[index].start;
			} else {
				day[index].size = (23 / 24) - day[index].start;
			}
		}
	}));
	return days;
}

function downloadAsJson(data, name) {
	name = name.replace(/[/|\\:*?"<>]/g, ' ');

	const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(data, null, 2));
	const downloadAnchorNode = document.createElement('a');
	downloadAnchorNode.setAttribute('href',     dataStr);
	downloadAnchorNode.setAttribute('download', name + '.json');
	document.body.appendChild(downloadAnchorNode);
	downloadAnchorNode.click();
	downloadAnchorNode.remove();
}
function downloadAsZip(data, name) {
	name = name.replace(/[/|\\:*?"<>]/g, ' ');
	const downloadAnchorNode = document.createElement('a');
	downloadAnchorNode.setAttribute('href',     URL.createObjectURL(data));
	downloadAnchorNode.setAttribute('download', name + '.zip');
	document.body.appendChild(downloadAnchorNode);
	downloadAnchorNode.click();
	downloadAnchorNode.remove();
}
export const {
	setProfile,
	setChannelId,
	setSelectedDay,
	setLocation,
	setCentralPowerControlId,
	setProfileData,
	setBaseProfileId,
	setZoneId,
	setLuminaryId,
	downloadProfile,
	downloadZip,
	downloadSimulationResult,
	setLoadedData,
	customUpload,
	setProjectName,
	setPicoPassword,
	resetCustomUpload,
	resetLightProfile,
	saveSimJson,
	saveCustomUploadStatus,
} = lightProfileSlice.actions;


export default lightProfileSlice.reducer;
